import { createSelector } from 'reselect'
import { LabRemoteConfigKeys } from 'constants/lab'
import { PAYLATER_PAYMENT_TYPE, PAYPAL_PAYMENT_TYPE } from 'constants/payment'
import {
  dateIsAfterOrSame,
  dateIsBeforeOrSame,
  isValidDate,
} from 'lib/datetime/dateUtils'
import { isRecord } from 'lib/object/objectUtils'
import { readTrimmedString, readTrimmedStringArray } from 'loyalty/lib/labPayloadUtils'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'

interface PayPalTriplePointsCampaignConfig {
  enabled: boolean
  earnKey: string
  regions: ReadonlyArray<string>
  startDate: Date
  endDate: Date
}

const DEFAULT_PAYPAL_TRIPLE_POINTS_CAMPAIGN = {
  earnKey: 'c=paypal-3x-au',
  regions: ['AU'],
  enabled: false,
  startDate: new Date('2026-08-01T00:00:00.000+10:00'),
  endDate: new Date('2026-08-31T23:59:59.999+10:00'),
}

function normalizeRegionCode(regionCode: string): string {
  return regionCode.trim().toUpperCase()
}

function parsePayPalTriplePointsCampaign(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): PayPalTriplePointsCampaignConfig {
  if (!remoteConfig || remoteConfig.error || !isRecord(remoteConfig.value)) {
    return {
      ...DEFAULT_PAYPAL_TRIPLE_POINTS_CAMPAIGN,
    }
  }

  const { value } = remoteConfig
  const earnKey = readTrimmedString(value.earnKey) ?? DEFAULT_PAYPAL_TRIPLE_POINTS_CAMPAIGN.earnKey
  const parsedRegions = readTrimmedStringArray(value.regions)?.map(normalizeRegionCode)
  const regions = parsedRegions && parsedRegions.length > 0
    ? parsedRegions
    : [...DEFAULT_PAYPAL_TRIPLE_POINTS_CAMPAIGN.regions]

  const startDateString = readTrimmedString(value.startDate)
  const startDate = startDateString && isValidDate(new Date(startDateString))
    ? new Date(startDateString)
    : DEFAULT_PAYPAL_TRIPLE_POINTS_CAMPAIGN.startDate

  const endDateString = readTrimmedString(value.endDate)
  const endDate = endDateString && isValidDate(new Date(endDateString))
    ? new Date(endDateString)
    : DEFAULT_PAYPAL_TRIPLE_POINTS_CAMPAIGN.endDate

  return {
    enabled: value.enabled === true,
    earnKey,
    regions,
    startDate,
    endDate,
  }
}

const getPayPalTriplePointsCampaignConfig = createSelector(
  (state: App.State) => state.system.features.remoteConfigFlags?.[LabRemoteConfigKeys.loyaltyPaypalTriplePointCampaign],
  parsePayPalTriplePointsCampaign,
)

function isPayPalTriplePointsCampaignActive(
  campaign: PayPalTriplePointsCampaignConfig | undefined,
  regionCode: string,
  now = new Date(),
): boolean {
  if (!campaign?.enabled) {
    return false
  }

  if (!isValidDate(campaign.startDate) || !isValidDate(campaign.endDate)) {
    return false
  }

  return dateIsAfterOrSame(now, campaign.startDate)
    && dateIsBeforeOrSame(now, campaign.endDate)
    && campaign.regions.includes(normalizeRegionCode(regionCode))
}

export const getIsPayPalTriplePointsCampaignActive = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  getPayPalTriplePointsCampaignConfig,
  getHasLoyaltyAccount,
  (currentRegionCode, campaign, hasLoyaltyAccount): boolean => {
    if (!hasLoyaltyAccount) {
      return false
    }

    return isPayPalTriplePointsCampaignActive(campaign, currentRegionCode)
  },
)

export const getPayPalTriplePointsEarnKeyMarker = createSelector(
  (state: App.State) => state.checkout.displayedPaymentMethod,
  getIsPayPalTriplePointsCampaignActive,
  getPayPalTriplePointsCampaignConfig,
  (selectedPaymentMethod, isCampaignActive, campaign): string | undefined => {
    const isPayPalPaymentMethod = selectedPaymentMethod === PAYPAL_PAYMENT_TYPE
      || selectedPaymentMethod === PAYLATER_PAYMENT_TYPE

    if (!isCampaignActive || !isPayPalPaymentMethod) {
      return undefined
    }

    return campaign.earnKey
  },
)

export default getPayPalTriplePointsCampaignConfig
